import { useMemo, useState } from "react";
import styles from "./budget.module.css";

const FREQS = ["Weekly", "Fortnightly", "Monthly", "Annually"];
const fmt = new Intl.NumberFormat("en-AU", {
  style: "currency",
  currency: "AUD",
  maximumFractionDigits: 0,
});

/* Convert any frequency to monthly */
const toMonthly = (amt, freq) => {
  const a = Number(amt) || 0;
  switch (freq) {
    case "Weekly": return (a * 52) / 12;
    case "Fortnightly": return (a * 26) / 12;
    case "Annually": return a / 12;
    default: return a; // Monthly
  }
};

/* ---------- AU resident income tax (FY 2024–25 stage-3) + simple 2% Medicare levy ---------- */
function auIncomeTax2024_25(grossAnnual) {
  const g = Math.max(0, Number(grossAnnual) || 0);

  const brackets = [
    { upTo: 18200,  rate: 0.00 },
    { upTo: 45000,  rate: 0.16 },
    { upTo: 135000, rate: 0.30 },
    { upTo: 190000, rate: 0.37 },
    { upTo: Infinity, rate: 0.45 },
  ];

  let tax = 0;
  let prev = 0;
  for (const { upTo, rate } of brackets) {
    if (g <= prev) break;
    const taxable = Math.min(g, upTo) - prev;
    if (taxable > 0) tax += taxable * rate;
    prev = upTo;
  }

  // Simple 2% Medicare levy (doesn't apply low-income reductions)
  const medicare = g * 0.02;
  return tax + medicare;
}

export default function BudgetCalculator({
  repaymentAmount = 0,
  onClose,
}) {
  const [step, setStep] = useState("income"); // income | expenses | results

  // ---------- INCOME ----------
  const [incomes, setIncomes] = useState([
    { id: 1, label: "Applicant 1 salary", amount: "", freq: "Annually", required: true },
    { id: 2, label: "Applicant 2 salary (optional)", amount: "", freq: "Annually", required: false },
  ]);

  const addIncome = () =>
    setIncomes((rows) => [
      ...rows,
      { id: Date.now(), label: "Other income", amount: "", freq: "Annually", required: false },
    ]);

  const removeIncome = (id) => setIncomes((rows) => rows.filter((r) => r.id !== id));
  const setIncomeField = (id, key, val) =>
    setIncomes((rows) => rows.map((r) => (r.id === id ? { ...r, [key]: val } : r)));

  // Gross monthly & annual from entered rows
  const grossMonthlyIncome = useMemo(
    () => incomes.reduce((sum, r) => sum + toMonthly(r.amount, r.freq), 0),
    [incomes]
  );
  const annualIncomeGross = grossMonthlyIncome * 12;

  // Apply AU tax -> net monthly income
  const annualTax = useMemo(() => auIncomeTax2024_25(annualIncomeGross), [annualIncomeGross]);
  const netAnnualIncome = Math.max(0, annualIncomeGross - annualTax);
  const netMonthlyIncome = netAnnualIncome / 12;

  // ---------- EXPENSES ----------
  const [expenses, setExpenses] = useState([
    { id: "groceries",      label: "Groceries",                              amount: "", freq: "Monthly" },
    { id: "utilities",      label: "Utilities (electricity, gas, water)",    amount: "", freq: "Monthly" },
    { id: "phone_net",      label: "Phone & Internet",                       amount: "", freq: "Monthly" },
    { id: "transport_fuel", label: "Transport / Fuel",                       amount: "", freq: "Monthly" },
    { id: "car_ins_rego",   label: "Car insurance & rego",                   amount: "", freq: "Monthly" },
    { id: "health_ins",     label: "Health insurance",                       amount: "", freq: "Monthly" },
    { id: "entertain",      label: "Entertainment & dining",                 amount: "", freq: "Monthly" },
    { id: "subscriptions",  label: "Subscriptions (Netflix, etc.)",          amount: "", freq: "Monthly" },
    { id: "other_exp",      label: "Other expenses",                         amount: "", freq: "Monthly" },
  ]);

  const addExpense = () =>
    setExpenses((rows) => [
      ...rows,
      { id: Date.now(), label: "Other expenses", amount: "", freq: "Monthly" },
    ]);

  const removeExpense = (id) => setExpenses((rows) => rows.filter((r) => r.id !== id));
  const setExpenseField = (id, key, val) =>
    setExpenses((rows) => rows.map((r) => (r.id === id ? { ...r, [key]: val } : r)));

  const monthlyExpenses = useMemo(
    () => expenses.reduce((sum, r) => sum + toMonthly(r.amount, r.freq), 0),
    [expenses]
  );

  // ---------- RESULTS ----------
  const monthlyRepay = Number(repaymentAmount) || 0;
  // Use NET monthly income for leftover
  const leftover = Math.max(0, netMonthlyIncome - monthlyExpenses - monthlyRepay);

  const verdictClass =
    leftover > 2000 ? styles.good : leftover >= 500 ? styles.neutral : styles.warn;
  const verdictText =
    leftover > 2000
      ? "Based on the figures you entered, you would have a strong buffer after repayments."
      : leftover >= 500
      ? "Based on the figures you entered, there would be some buffer left after repayments."
      : "Based on the figures you entered, the buffer after repayments appears low.";

  const applicant1Valid = (Number(incomes[0]?.amount) || 0) > 0;

  return (
    <div className={styles.overlay} role="dialog" aria-modal="true">
      <div className={styles.shell}>
        {/* Header */}
        <div className={styles.header}>
          <h2 className={styles.title}>Budget Calculator</h2>
          <button className={styles.close} onClick={onClose} aria-label="Close">×</button>
        </div>

        {/* Steps */}
        <div className={styles.steps}>
          <div className={`${styles.badge} ${step === "income" ? styles.badgeActive : ""}`}>Income</div>
          <div className={`${styles.badge} ${step === "expenses" ? styles.badgeActive : ""}`}>Expenses</div>
          <div className={`${styles.badge} ${step === "results" ? styles.badgeActive : ""}`}>Results</div>
        </div>

        {/* Panel */}
        <div className={styles.panel}>
          {/* ---------- INCOME PANEL ---------- */}
          {step === "income" && (
            <>
              <div className={styles.rows}>
                {incomes.map((r, idx) => (
                  <div className={styles.row} key={r.id}>
                    <input
                      className={`${styles.name} ${styles.input || ""}`}
                      value={r.label}
                      onChange={(e) => setIncomeField(r.id, "label", e.target.value)}
                      placeholder="Description"
                    />
                    <input
                      className={styles.amount}
                      inputMode="numeric"
                      value={r.amount}
                      onChange={(e) =>
                        setIncomeField(r.id, "amount", e.target.value.replace(/[^\d.]/g, ""))
                      }
                      placeholder="0"
                    />
                    <select
                      className={styles.freq}
                      value={r.freq}
                      onChange={(e) => setIncomeField(r.id, "freq", e.target.value)}
                    >
                      {FREQS.map((f) => (
                        <option key={f} value={f}>{f}</option>
                      ))}
                    </select>
                    <button
                      className={styles.remove}
                      onClick={() => removeIncome(r.id)}
                      disabled={idx === 0 || idx === 1}
                      title={idx < 2 ? "Can't remove default applicant rows" : "Remove"}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>

              <div className={styles.footerNav}>
                <span />
                <button
                  className={`${styles.btn} ${styles.btnPrimary} ${styles.btnSmall}`}
                  onClick={addIncome}
                >
                  Add income
                </button>
              </div>

              <div className={styles.ctaRow}>
                <div className={styles.repaymentBox}>
                  <div className={styles.label}>Repayment</div>
                  <div className={styles.readOnly}>
                    <span>{fmt.format(monthlyRepay)}</span>
                    <span>/ mo</span>
                  </div>
                </div>

                <button
                  className={`${styles.btn} ${styles.btnSecondary}`}
                  onClick={() => applicant1Valid && setStep("expenses")}
                  disabled={!applicant1Valid}
                  aria-disabled={!applicant1Valid}
                >
                  Next: Expenses →
                </button>
              </div>
            </>
          )}

          {/* ---------- EXPENSES PANEL ---------- */}
          {step === "expenses" && (
            <>
              <div className={styles.rows}>
                {expenses.map((r) => (
                  <div className={styles.row} key={r.id}>
                    <input
                      className={`${styles.name} ${styles.input || ""}`}
                      value={r.label}
                      onChange={(e) => setExpenseField(r.id, "label", e.target.value)}
                      placeholder="Expense description"
                    />
                    <input
                      className={styles.amount}
                      inputMode="numeric"
                      value={r.amount}
                      onChange={(e) => setExpenseField(r.id, "amount", e.target.value.replace(/[^\d.]/g, ""))}
                      placeholder="0"
                    />
                    <select
                      className={styles.freq}
                      value={r.freq}
                      onChange={(e) => setExpenseField(r.id, "freq", e.target.value)}
                    >
                      {FREQS.map((f) => (
                        <option key={f} value={f}>{f}</option>
                      ))}
                    </select>
                    <button className={styles.remove} onClick={() => removeExpense(r.id)}>✕</button>
                  </div>
                ))}
              </div>

              <div className={styles.footerNav}>
                <button className={`${styles.btn} ${styles.btnSecondary}`} onClick={() => setStep("income")}>
                  ← Back to Income
                </button>
                <button className={`${styles.btn} ${styles.btnPrimary} ${styles.btnSmall}`} onClick={addExpense}>
                  Add expense
                </button>
              </div>

              <div className={styles.ctaRow}>
                <div className={styles.repaymentBox}>
                  <div className={styles.label}>Repayment</div>
                  <div className={styles.readOnly}>
                    <span>{fmt.format(monthlyRepay)}</span>
                    <span>/ mo</span>
                  </div>
                </div>

                <button className={`${styles.btn} ${styles.btnSecondary}`} onClick={() => setStep("results")}>
                  View Results →
                </button>
              </div>
            </>
          )}

          {/* ---------- RESULTS PANEL ---------- */}
          {step === "results" && (
            <>
              <div className={styles.summaryGrid}>
                <div className={styles.statCard}>
                  <div className={styles.statLabel}>Gross annual income</div>
                  <div className={styles.statValue}>{fmt.format(annualIncomeGross)}</div>
                </div>
                <div className={styles.statCard}>
                  <div className={styles.statLabel}>Net monthly income (after tax)</div>
                  <div className={styles.statValue}>{fmt.format(netMonthlyIncome)}</div>
                </div>
                <div className={styles.statCard}>
                  <div className={styles.statLabel}>Monthly expenses</div>
                  <div className={styles.statValue}>{fmt.format(monthlyExpenses)}</div>
                </div>
                <div className={styles.statCard}>
                  <div className={styles.statLabel}>Your repayment</div>
                  <div className={styles.statValue}>{fmt.format(monthlyRepay)}</div>
                </div>
                <div className={styles.statCard}>
                  <div className={styles.statLabel}>Left over</div>
                  <div className={styles.statValue}>{fmt.format(leftover)}</div>
                </div>
              </div>

              <div className={`${styles.verdict} ${verdictClass}`}>{verdictText}</div>

              <p className={styles.disclaimerNote}>
                This tool provides general information only and is not personal financial advice.
                Outcomes depend on your full circumstances and lender assessment. Please speak with a broker for tailored guidance.
              </p>

              <div className={styles.ctaRow}>
                <div />
                <a
                  href="https://www.mortgagematrix.com.au/get-started"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${styles.btn} ${styles.btnPrimary}`}
                >
                  Talk to your broker →
                </a>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
